import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "../pd-wizard.js";
import { WizardReloadPopup } from "../wizard-reload-popup/WizardReloadPopup.js";
import { WizardClosePopup } from "../wizard-close-popup/WizardClosePopup.js";
import { LitElement, css, html } from "lit";
import { state } from "lit/decorators.js";
import { msg } from "@lit/localize";
import "@progressive-development/pd-dialog/pd-popup-dialog";
import { EVENT_CANCEL, EVENT_CONFIRM } from "@progressive-development/pd-dialog";
//#region src/default-wizard/DefaultWizard.ts
/**
* DefaultWizard - Abstract base class for wizard implementations
*
* Provides a complete wizard framework with:
* - Step navigation (next, previous, go-to)
* - Form validation per step
* - Optional localStorage persistence
* - Close/reload confirmation dialogs
*
* @example
* ```typescript
* interface OrderFormData {
*   name: string;
*   email: string;
*   product: string;
* }
*
* @customElement("my-order-wizard")
* export class MyOrderWizard extends DefaultWizard<OrderFormData> {
*   _wizardSteps: WizardStep[] = [
*     { key: "contact", name: "Contact", title: "Your Details" },
*     { key: "product", name: "Product", title: "Select Product" },
*     { key: "summary", name: "Summary", title: "Review Order" },
*   ];
*
*   _withLocalStorageId = "my-order-wizard";
*
*   _renderSteps() {
*     return html`
*       <my-contact-step id="wiz1Id"></my-contact-step>
*       <my-product-step id="wiz2Id"></my-product-step>
*       <my-summary-step id="wiz3Id"></my-summary-step>
*     `;
*   }
*
*   _closeWizard() {
*     window.history.back();
*   }
*
*   _submitWizard() {
*     console.log("Submit:", this._orderFormData);
*   }
* }
* ```
*/
var RUNNING_WIZARD_STORAGE_ELEMENTS = "pd.wizard.local.storage";
/**
* Abstract base class for wizard implementations.
*
* @typeParam T - The form data type collected across all steps
*/
var DefaultWizard = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this._scrollToTopOnStepChange = true;
		this._currentOrderStep = 1;
		this._maxReachedStep = 1;
		this._withEarlySubmit = false;
		this._withDirtyGuard = false;
		this._dirtyGuardBlocked = false;
		this._submitting = false;
	}
	static {
		this.styles = css`
    :host {
      display: block;
    }
  `;
	}
	update(changedProps) {
		if (changedProps.has("_withLocalStorageId") && this._withLocalStorageId) this._reloadFromStorage(this._withLocalStorageId);
		super.update(changedProps);
	}
	render() {
		return html`
      <pd-wizard
        id="wizardId"
        currentNumber="${this._currentOrderStep}"
        .wizardSteps="${this._wizardSteps}"
        .submitDisabled="${this._submitting || this._dirtyGuardBlocked}"
        .withEarlySubmit="${this._withEarlySubmit}"
        @next-step="${this._nextStep}"
        @previous-step="${this._previousStep}"
        @submit-wizard="${this._handleSubmitWizard}"
        @pd-form-change="${this._updateDirtyGuard}"
        @go-to="${this._goToStep}"
        @close-wizard="${this._closeWizardRequest}"
        @wizard-step-update="${this._doStorageEventUpdate}"
      >
        ${this._renderSteps()}
      </pd-wizard>
    `;
	}
	_nextStep(e) {
		const setNextStep = () => {
			if (this._currentOrderStep < this._wizardSteps.length) {
				this._currentOrderStep += 1;
				this._maxReachedStep = Math.max(this._maxReachedStep, this._currentOrderStep);
				if (this._withLocalStorageId) this._doStorageUpdate();
				this._notifyStepEnter(this._currentOrderStep);
				this._scrollToTop();
			}
		};
		if (this._commitCurrentStep()) setNextStep();
		e.stopPropagation();
	}
	/**
	* Handles the `submit-wizard` event (fired on the last step, or on any
	* step when `_withEarlySubmit` is enabled): validates the current step,
	* merges its data and runs `_submitWizard()` behind the
	* double-submission guard.
	*
	* With `_withEarlySubmit`, the data of every other valid step is merged
	* first. All steps stay mounted (hidden via display:none), so in edit
	* flows they hold their prefilled values — without this merge, an early
	* submit would send a partial payload and could wipe the data of steps
	* the user never visited. Invalid or not-yet-mounted steps are skipped;
	* the current step is committed last, so it wins on key conflicts.
	*/
	_handleSubmitWizard(e) {
		e.stopPropagation();
		if (this._computeDirtyGuardBlocked()) return;
		if (this._withEarlySubmit && this._getStepElement(this._currentOrderStep)?.valid) this._mergeOtherValidSteps();
		if (this._commitCurrentStep()) this._startSubmit();
	}
	/** Merges the data of every valid step except the current one into `_orderFormData`. */
	_mergeOtherValidSteps() {
		for (let stepNr = 1; stepNr <= this._wizardSteps.length; stepNr += 1) {
			if (stepNr === this._currentOrderStep) continue;
			const stepEl = this._getStepElement(stepNr);
			if (stepEl?.valid) this._orderFormData = stepEl.setFormData(this._orderFormData);
		}
	}
	/**
	* Validates the current step and, if valid, merges its data into
	* `_orderFormData`. Triggers the step's validation display otherwise.
	*
	* @returns true if the step was valid and its data was merged
	*/
	_commitCurrentStep() {
		const currentStepEl = this._getStepElement(this._currentOrderStep);
		if (currentStepEl?.valid) {
			this._orderFormData = currentStepEl.setFormData(this._orderFormData);
			return true;
		}
		currentStepEl?.triggerValidate();
		return false;
	}
	/** Resolves the step element for a 1-based step number via the `wiz<n>Id` convention. */
	_getStepElement(stepNr) {
		return this.shadowRoot?.getElementById(`wiz${stepNr}Id`);
	}
	/**
	* Recomputes the dirty guard. Triggered by `pd-form-change`, which the
	* step forms dispatch on field registration and every value change —
	* so the guard engages as soon as the (prefilled) forms register and
	* releases on the first user input.
	*/
	_updateDirtyGuard() {
		this._dirtyGuardBlocked = this._computeDirtyGuardBlocked();
	}
	/**
	* The guard blocks when it is enabled, at least one step supports dirty
	* tracking, and none of the supporting steps is dirty. Steps without
	* dirty support are ignored; if no step supports it, the guard is
	* inactive (fail-open) so such wizards never lock up.
	*/
	_computeDirtyGuardBlocked() {
		if (!this._withDirtyGuard) return false;
		let anySupported = false;
		for (let stepNr = 1; stepNr <= this._wizardSteps.length; stepNr += 1) {
			const dirty = this._getStepElement(stepNr)?.dirty;
			if (typeof dirty === "boolean") {
				anySupported = true;
				if (dirty) return false;
			}
		}
		return anySupported;
	}
	_previousStep(e) {
		if (this._currentOrderStep > 1) {
			this._currentOrderStep -= 1;
			this._notifyStepEnter(this._currentOrderStep);
			this._scrollToTop();
		}
		e.stopPropagation();
	}
	_goToStep(e) {
		const stepNr = e.detail.step;
		if (stepNr > 0 && stepNr <= this._wizardSteps.length) {
			this._currentOrderStep = Number(stepNr);
			this._notifyStepEnter(this._currentOrderStep);
			this._scrollToTop();
		}
		e.stopPropagation();
	}
	/**
	* Runs `_submitWizard()` guarded against double submission:
	* disables the submit button synchronously before invoking it and
	* re-enables it when a returned promise rejects or the call throws.
	*/
	_startSubmit() {
		if (this._submitting) return;
		this._submitting = true;
		try {
			const result = this._submitWizard();
			if (result instanceof Promise) result.catch((error) => {
				this._logger?.error("Wizard submit failed", error);
				this._resetSubmit();
			});
		} catch (error) {
			this._logger?.error("Wizard submit failed", error);
			this._resetSubmit();
			throw error;
		}
	}
	/**
	* Re-enables the submit button after a failed submit.
	* Call this from the subclass when a fire-and-forget submit
	* (e.g. store dispatch) reports a failure.
	*/
	_resetSubmit() {
		this._submitting = false;
	}
	/**
	* Notify a step element that it has become active.
	* Calls `onStepEnter()` with the current accumulated form data.
	*/
	_notifyStepEnter(stepNr) {
		const stepEl = this.shadowRoot?.getElementById(`wiz${stepNr}Id`);
		if (stepEl && typeof stepEl.onStepEnter === "function") stepEl.onStepEnter(this._orderFormData ?? {});
	}
	/** Scroll to top of page after step change. Disabled when `_scrollToTopOnStepChange` is false. */
	_scrollToTop() {
		if (!this._scrollToTopOnStepChange) return;
		window.setTimeout(() => {
			window.scrollTo({
				top: 0,
				left: 0,
				behavior: "smooth"
			});
		}, 100);
	}
	_closeWizardRequest() {
		if (this._maxReachedStep === 1) {
			this._closeWizard();
			return;
		}
		if (this._withLocalStorageId) {
			const popup = new WizardClosePopup();
			popup.withStorage = true;
			popup.confirmButtonText = msg("Speichern", { id: "pdWizard.storage.close.popup.ok" });
			popup.cancelButtonText = msg("Verwerfen", { id: "pdWizard.storage.close.popup.cancel" });
			popup.addEventListener(EVENT_CANCEL, () => {
				this._removeFromStorage();
				this.shadowRoot?.removeChild(popup);
				this._closeWizard();
			});
			popup.addEventListener(EVENT_CONFIRM, () => {
				this.shadowRoot?.removeChild(popup);
				this._closeWizard();
			});
			this.shadowRoot?.appendChild(popup);
		} else {
			const popup = new WizardClosePopup();
			popup.withStorage = false;
			popup.confirmButtonText = msg("Verwerfen", { id: "pdWizard.close.popup.ok" });
			popup.cancelButtonText = msg("Zurück", { id: "pdWizard.close.popup.cancel" });
			popup.addEventListener(EVENT_CANCEL, () => {
				this.shadowRoot?.removeChild(popup);
			});
			popup.addEventListener(EVENT_CONFIRM, () => {
				this.shadowRoot?.removeChild(popup);
				this._closeWizard();
			});
			this.shadowRoot?.appendChild(popup);
		}
	}
	_reloadFromStorage(storageKey) {
		this._logger?.debug(`call _reloadFromStorage ${storageKey}`);
		if (storageKey) {
			const storageEl = this.getWizardStorage()[storageKey];
			this._logger?.debug("reload storageEl", storageEl);
			if (storageEl && storageEl.orderFormData !== void 0) {
				const popup = new WizardReloadPopup();
				popup.reloadItem = storageEl;
				popup.confirmButtonText = msg("Fortsetzen", { id: "pdWizard.reload.popup.ok" });
				popup.cancelButtonText = msg("Verwerfen", { id: "pdWizard.reload.popup.cancel" });
				popup.addEventListener(EVENT_CANCEL, () => {
					this._removeFromStorage();
					this.shadowRoot?.removeChild(popup);
				});
				popup.addEventListener(EVENT_CONFIRM, () => {
					this._logger?.debug(`Reload storage element from ${storageEl.timestamp}`);
					this._orderFormData = storageEl.orderFormData;
					this._currentOrderStep = storageEl.wizardStep;
					this._maxReachedStep = storageEl.wizardStep;
					this.shadowRoot?.removeChild(popup);
					for (let stepNr = 1; stepNr <= this._wizardSteps.length; stepNr += 1) {
						const stepId = `wiz${stepNr}Id`;
						const stepElement = this.shadowRoot?.getElementById(stepId);
						if (stepElement && typeof stepElement.reloadFromOrder === "function") stepElement.reloadFromOrder(this._orderFormData);
					}
				});
				this.shadowRoot?.appendChild(popup);
			}
		}
	}
	_doStorageUpdate(preparedOrderForm) {
		if (this._withLocalStorageId) {
			const storageWizard = this.getWizardStorage();
			storageWizard[this._withLocalStorageId] = {
				orderFormData: preparedOrderForm || this._orderFormData,
				wizardStep: this._currentOrderStep,
				timestamp: (/* @__PURE__ */ new Date()).toUTCString()
			};
			localStorage.setItem(RUNNING_WIZARD_STORAGE_ELEMENTS, JSON.stringify(storageWizard));
			this._logger?.debug(`Update wizard local storage for key ${this._withLocalStorageId}`);
		}
	}
	_removeFromStorage() {
		if (this._withLocalStorageId) {
			const storageWizard = this.getWizardStorage();
			delete storageWizard[this._withLocalStorageId];
			localStorage.setItem(RUNNING_WIZARD_STORAGE_ELEMENTS, JSON.stringify(storageWizard));
			this._logger?.debug(`Remove wizard local storage for key ${this._withLocalStorageId}`);
		}
	}
	getWizardStorage() {
		const storageEl = localStorage.getItem(RUNNING_WIZARD_STORAGE_ELEMENTS);
		return storageEl ? JSON.parse(storageEl) : {};
	}
	_doStorageEventUpdate(event) {
		if (!this._withLocalStorageId) return;
		if (event.detail) {
			this._logger?.debug("event detail set, store prepared data: ", event.detail);
			const preparedOrder = {
				...this._orderFormData || {},
				...event.detail
			};
			this._doStorageUpdate(preparedOrder);
		}
		event.stopPropagation();
	}
};
__decorate([state()], DefaultWizard.prototype, "_currentOrderStep", void 0);
__decorate([state()], DefaultWizard.prototype, "_maxReachedStep", void 0);
__decorate([state()], DefaultWizard.prototype, "_orderFormData", void 0);
__decorate([state()], DefaultWizard.prototype, "_withLocalStorageId", void 0);
__decorate([state()], DefaultWizard.prototype, "_withEarlySubmit", void 0);
__decorate([state()], DefaultWizard.prototype, "_withDirtyGuard", void 0);
__decorate([state()], DefaultWizard.prototype, "_dirtyGuardBlocked", void 0);
__decorate([state()], DefaultWizard.prototype, "_submitting", void 0);
//#endregion
export { DefaultWizard };
